import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/**
 * Réglages de rémunération : valeurs de référence et barèmes.
 *
 * Aucun taux ni montant n'est écrit dans le front — ils viennent tous de
 * l'API, qui les lit en base. Ce fichier ne décrit que la forme des données.
 */

export interface ValeurReference {
  id: string;
  code: string;
  libelle: string;
  valeur: number;
  unite: string;
  dateEffet: string;
  source: string | null;
  saisiPar: string | null;
  /** Valeur qui s'applique aujourd'hui, parmi celles de son code. */
  enVigueur: boolean;
  /** Valeur saisie à l'avance, qui prendra effet le moment venu. */
  aVenir: boolean;
}

export interface LigneBareme {
  id: string;
  libelleTranche: string;
  ageMin: number | null;
  ageMax: number | null;
  rangAnnee: number | null;
  critere: string | null;
  pourcentage: number;
  base: 'SMIC' | 'MINIMUM_CONVENTIONNEL';
  ordre: number;
  /** Montant mensuel calculé par le serveur. Nul hors base SMIC. */
  montant: number | null;
}

export interface BaremeRemuneration {
  id: string;
  code: string;
  libelle: string;
  dateEffet: string;
  referenceCode: string;
  note: string;
  socle: boolean;
  enVigueur: boolean;
  aVenir: boolean;
  /** Périodes de contrat déjà calculées avec lui : au-delà de zéro, il est figé. */
  contratsCalcules: number;
  lignes: LigneBareme[];
}

export interface ParametresRemuneration {
  valeurs: ValeurReference[];
  /** Mensuel de référence, calculé : horaire × durée mensuelle. */
  mensuelDeReference: number | null;
  baremes: BaremeRemuneration[];
}

export interface EntreeValeurReference {
  code: string;
  libelle?: string;
  unite?: string;
  valeur: number;
  dateEffet: string;
  source?: string;
}

export interface EntreeBaremeRemuneration {
  code: string;
  libelle: string;
  dateEffet: string;
  referenceCode?: string;
  note?: string;
  lignes: {
    libelleTranche: string;
    ageMin: number | null;
    ageMax: number | null;
    rangAnnee: number | null;
    critere: string | null;
    pourcentage: number;
    base: 'SMIC' | 'MINIMUM_CONVENTIONNEL';
    ordre?: number;
  }[];
}

const CLE = ['parametres-remuneration'];

export function useParametresRemuneration() {
  return useQuery({
    queryKey: CLE,
    queryFn: () => appelApi<ParametresRemuneration>('/remuneration/parametres'),
  });
}

/**
 * Invalide tout ce qui dépend du barème.
 *
 * Une grille de contrat proposée, la rémunération légale d'un dossier et la
 * liste des contrats sont calculées à partir de ces réglages : les laisser en
 * cache afficherait l'ancien taux jusqu'au prochain rechargement complet.
 */
function invaliderDependances(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: CLE });
  void client.invalidateQueries({ queryKey: ['contrats'] });
  void client.invalidateQueries({ queryKey: ['dossier'] });
}

export function useEnregistrerValeurReference() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: EntreeValeurReference) =>
      appelApi<ParametresRemuneration>('/remuneration/valeurs', {
        methode: 'POST',
        corps: entree,
      }),
    onSuccess: () => invaliderDependances(client),
  });
}

export function useEnregistrerBareme() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: EntreeBaremeRemuneration) =>
      appelApi<ParametresRemuneration>('/remuneration/baremes', {
        methode: 'POST',
        corps: entree,
      }),
    onSuccess: () => invaliderDependances(client),
  });
}

export function useSupprimerBareme() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<ParametresRemuneration>(`/remuneration/baremes/${id}`, {
        methode: 'DELETE',
      }),
    onSuccess: () => invaliderDependances(client),
  });
}

/** Date civile réunionnaise, comme partout ailleurs dans l'application. */
export function jourReunion(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/** Date au format attendu par `<input type="date">`, en heure locale. */
export function jourFormulaire(valeur: string | Date = new Date()): string {
  return new Date(valeur).toLocaleDateString('fr-CA', { timeZone: 'Indian/Reunion' });
}

/** Montant affiché à l'euro et au centime, sans jamais être recalculé ici. */
export function montantEuros(valeur: number | null): string {
  if (valeur === null) return '—';
  return `${valeur.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
}
